import React, { createContext, useState, useContext, useEffect, useCallback} from 'react';
import chatApi from '../api/chatApi';
import { useAuth } from './AuthContext';

// Create context
const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await chatApi.getConversations();
      setConversations(data);
      setLoading(false);
      return data;
    } catch (err) {
      setLoading(false);
      setError('Failed to load conversations');
      console.error(err);
      return [];
    }
  }, []);  // Empty dependency array

  // Then update your useEffect
  useEffect(() => {
    if (isAuthenticated) {
      fetchConversations();
    }
  }, [isAuthenticated, fetchConversations]);

  // Create a new conversation
  const createConversation = async (title = null) => {
    try {
      setLoading(true);
      setError(null);
      const newConversation = await chatApi.createConversation(title);
      setConversations([newConversation, ...conversations]);
      setCurrentConversation(newConversation);
      setLoading(false);
      return newConversation.id;
    } catch (err) {
      setLoading(false);
      setError('Failed to create new conversation');
      console.error(err);
      return null;
    }
  };

  // Load a specific conversation with messages
  const loadConversation = async (conversationId) => {
    try {
      setLoading(true);
      setError(null);
      const conversation = await chatApi.getConversation(conversationId);
      
      // Ensure messages is always initialized as an array
      if (!conversation.messages) {
        conversation.messages = [];
      }
      
      setCurrentConversation(conversation);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError('Failed to load conversation');
      console.error(err);
    }
  };

  // Send a message to current conversation
  const sendMessage = async (content) => {
    if (!currentConversation) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await chatApi.sendMessage(currentConversation.id, content);
      
      // Update the current conversation with new messages
      setCurrentConversation({
        ...currentConversation,
        messages: [
          ...currentConversation.messages,
          ...response.messages
        ]
      });
      
      // Update conversation in the list (for updated timestamps)
      const updatedConversations = conversations.map(conv => 
        conv.id === currentConversation.id 
          ? {...conv, updated_at: new Date().toISOString()} 
          : conv
      );
      setConversations(updatedConversations);
      
      setLoading(false);
      return true;
    } catch (err) {
      setLoading(false);
      setError('Failed to send message');
      console.error(err);
      return false;
    }
  };

  // Delete a conversation
  const deleteConversation = async (conversationId) => {
    try {
      setLoading(true);
      setError(null);
      await chatApi.deleteConversation(conversationId);
      
      // Update conversations list
      const updatedConversations = conversations.filter(
        (conv) => conv.id !== conversationId
      );
      setConversations(updatedConversations);
      
      // If deleted conversation was current, clear it
      if (currentConversation && currentConversation.id === conversationId) {
        setCurrentConversation(null);
      }
      
      setLoading(false);
      return true;
    } catch (err) {
      setLoading(false);
      setError('Failed to delete conversation');
      console.error(err);
      return false;
    }
  };

  // Upload a folder to the conversation workspace
  const uploadFolder = async (conversationId, formData) => {
    try {
      setLoading(true);
      setError(null);
      const result = await chatApi.uploadFolder(conversationId, formData);
      setLoading(false);
      return result;
    } catch (err) {
      setLoading(false);
      setError('Failed to upload folder');
      console.error(err);
      throw err;
    }
  };

  const value = {
    conversations,
    currentConversation,
    loading,
    error,
    fetchConversations,
    createConversation,
    loadConversation,
    sendMessage,
    deleteConversation,
    uploadFolder,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// Custom hook to use chat context
export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export default ChatContext;